import React, { useState, useEffect, useMemo, useCallback } from "react";

// ---------- MUI core (single import; duplicates removed) ----------
import {
  Alert,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme, // MUI's useTheme replaces the @emotion/react one (same theme under MUI's ThemeProvider)
} from "@mui/material";

// ---------- MUI X data grid ----------
import {
  DataGrid,
  GridToolbar,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";

// ---------- Icons ----------
import {
  CheckBox,
  FilterList as FilterListIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import StoreIcon from "@mui/icons-material/Store";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import EditIcon from "@mui/icons-material/Edit";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

// ---------- Forms / alerts / theme ----------
import { Formik, Field } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import { tokens } from "../../theme";

// ---------- Config (inventoryManagementUrl merged into the existing import) ----------
import {
  AccountsandFinanceurl,
  caseManagementUrl,
  companyManagementUrl,
  documentManagementUrl,
  emailService,
  inventoryManagementUrl,
  salesManagement,
  smsservice,
  uploadCaseForClientService,
  uploadCaseService,
  uploadCorporateClients,
  uploadIndividualClients,
} from "../../config";

// ---------- Services / helpers ----------
import {
  generateAndExecuteMutation,
  fetchDataEngine,
  graphqlQuery,
  generateAndExecuteBulkMutation,
} from "../Axios/DynamicService";
import { allCaseStatus } from "../Axios/queries";
import {
  getDocumentAccessPermissions,
  getDocumentAccessPermissionsById,
} from "../Axios/Non-DynamicServices";
import {
  downloadCaseFile,
  downloadCaseForClientFile,
  downloadCorporateClientFile,
  downloadIndividualClientFile,
  uploadCasefile,
  uploadCaseForClientfile,
  uploadCorporateClient,
  uploadIndividualClient,
} from "./DynamicFunctions";



export function DistributionViewModal(props) {
  const { open, onClose, itemData } = props;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [distributionData, setDistributionData] = useState([]);

  const base_url = inventoryManagementUrl.uri;

  useEffect(() => {
    if (open && itemData) {
      // Ensure we have the required data to make the query
      if (!itemData.productCode || !itemData.branchId || !itemData.productId) {
        setError("Incomplete data. Cannot fetch distribution details.");
        setLoading(false);
        return;
      }

      const fetchDetails = async () => {
        setLoading(true);
        setError(null);

        // This query is based on the one you provided in the prompt
        const query = `
          query {
            inventoryDetailsByProductCode(inventory: { 
              productCode: "${itemData.productCode}", 
              branchId: ${itemData.branchId}, 
              productId: ${itemData.productId} 
            }) {
              id
              productName
              colorName
              sizeRange
              minSize
              maxSize
              quantity
              quantityPerCarton
              branchName
            }
          }
        `;

        try {
          const response = await graphqlQuery(query, base_url);
          if (response) {
            setDistributionData(response);
          } else {
            setDistributionData([]);
          }
        } catch (err) {
          setError(err.message || "An error occurred while fetching details.");
          console.error(err);
        } finally {
          setLoading(false);
        }
      };

      fetchDetails();
    }
  }, [open, itemData]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          color: colors.grey[100],
          borderBottom: `1px solid ${colors.grey[700]}`,
        }}
      >
        Distribution Details
        <Typography variant="body2" color="text.secondary">
          {itemData?.productName} ({itemData?.productCode})
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Color</TableCell>
                  <TableCell>Size Range</TableCell>
                  <TableCell>Min Size</TableCell>
                  <TableCell>Max Size</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Qty/Carton</TableCell>
                  <TableCell>Branch</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {distributionData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No distribution details found for this item.
                    </TableCell>
                  </TableRow>
                ) : (
                  distributionData.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.colorName}</TableCell>
                      <TableCell>{row.sizeRange}</TableCell>
                      <TableCell>{row.minSize}</TableCell>
                      <TableCell>{row.maxSize}</TableCell>
                      <TableCell>{row.quantity}</TableCell>
                      <TableCell>{row.quantityPerCarton}</TableCell>
                      <TableCell>{row.branchName}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}


export function AccountSummaryViewModal(props) {
  const { open, onClose, itemData } = props;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState([]);

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    accountType: "",
    branch: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  // Selected rows (single checkbox per row)
  const [selectedRows, setSelectedRows] = useState([]);

  const base_url = AccountsandFinanceurl.uri;

  useEffect(() => {
    if (open && itemData) {
      const accountId = itemData.id || itemData.accountID || itemData.AccountID;
      const accountSubcategoryId =
        itemData.accountSubcategoryId ||
        itemData.AccountSubcategoryId ||
        itemData.subcategoryId;

      const identifierType =
        itemData.identifierType ||
        itemData.IdentifierType ||
        itemData.type;

      if (!accountId && !accountSubcategoryId) {
        setError("Incomplete data. Cannot fetch Account Summary details.");
        setLoading(false);
        return;
      }

      const fetchDetails = async () => {
        setLoading(true);
        setError(null);

        const query = `
          query VwJournalLinesViewByColumnValues {
            vwJournalLinesViewByColumnValues(
                accountCategory: { accountID: ${accountId} }
            ) {
                id
                journalEntryID
                accountName
                accountNumber
                accountSubCategoryName
                accountCategoryName
                debit
                credit
                description
                createdDate
                createdBy
                applicationType
                cancelled
                branchName
            }
          }
        `;

        const groupQuery = `
          query VwJournalLinesViewByColumnValues {
            vwJournalLinesViewByColumnValues(
                accountCategory: { accountSubcategoryId: ${accountSubcategoryId} }
            ) {
                id
                journalEntryID
                accountName
                accountNumber
                accountSubCategoryName
                accountCategoryName
                debit
                credit
                description
                createdDate
                createdBy
                applicationType
                cancelled
                branchName
            }
          }
        `;

        try {
          const response = await graphqlQuery(
            identifierType === 1 ? query : groupQuery,
            base_url
          );

          let journalData = [];
          if (Array.isArray(response)) {
            journalData = response;
          } else if (response?.vwJournalLinesViewByColumnValues) {
            journalData = response.vwJournalLinesViewByColumnValues;
          }

          setSummaryData(Array.isArray(journalData) ? journalData : []);
        } catch (err) {
          setError(err.message || "An error occurred while fetching account summary details.");
        } finally {
          setLoading(false);
        }
      };

      fetchDetails();
    } else {
      setSummaryData([]);
      setSelectedRows([]);
      setSearchText("");
      setFilters({ accountType: "", branch: "", status: "", dateFrom: "", dateTo: "" });
    }
  }, [open, itemData]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '-';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getDebitColor = () => colors?.green?.[500] || '#4caf50';
  const getCreditColor = () => colors?.red?.[500] || '#f44336';
  const getBorderColor = () => colors?.grey?.[700] || '#e0e0e0';

  const uniqueBranches = useMemo(() => {
    const branches = [...new Set(summaryData.map(row => row.branchName).filter(Boolean))];
    return branches.sort();
  }, [summaryData]);

  const uniqueAccountTypes = useMemo(() => {
    const types = [...new Set(summaryData.map(row => row.accountSubCategoryName).filter(Boolean))];
    return types.sort();
  }, [summaryData]);

  const filteredData = useMemo(() => {
    let filtered = [...summaryData];

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(row =>
        String(row.description || "").toLowerCase().includes(search) ||
        String(row.journalEntryID || "").toLowerCase().includes(search) ||
        String(row.branchName || "").toLowerCase().includes(search) ||
        String(row.applicationType || "").toLowerCase().includes(search) ||
        String(row.createdBy || "").toLowerCase().includes(search)
      );
    }

    if (filters.accountType) filtered = filtered.filter(row => row.accountSubCategoryName === filters.accountType);
    if (filters.branch) filtered = filtered.filter(row => row.branchName === filters.branch);
    if (filters.status) {
      filtered = filtered.filter(row => {
        const isCancelled = row.cancelled === true;
        return filters.status === "active" ? !isCancelled : isCancelled;
      });
    }
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(row => {
        const rowDate = new Date(row.createdDate);
        rowDate.setHours(0, 0, 0, 0);
        return rowDate >= fromDate;
      });
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(row => {
        const rowDate = new Date(row.createdDate);
        rowDate.setHours(0, 0, 0, 0);
        return rowDate <= toDate;
      });
    }

    return filtered;
  }, [summaryData, searchText, filters]);

  const totals = useMemo(() => {
    return filteredData.reduce((acc, row) => {
      acc.totalDebit += Number(row.debit) || 0;
      acc.totalCredit += Number(row.credit) || 0;
      acc.balance += (Number(row.debit) || 0) - (Number(row.credit) || 0);
      return acc;
    }, { totalDebit: 0, totalCredit: 0, balance: 0 });
  }, [filteredData]);

  const activeFilterCount = useMemo(() => {
    return (searchText ? 1 : 0) + Object.values(filters).filter(v => v !== "").length;
  }, [searchText, filters]);

  const clearAllFilters = () => {
    setSearchText("");
    setFilters({ accountType: "", branch: "", status: "", dateFrom: "", dateTo: "" });
  };

  const columns = [
    // Single checkbox per row only
    {
      field: 'selection',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Checkbox
          checked={selectedRows.includes(params.row.id)}
          onChange={(event) => {
            if (event.target.checked) {
              setSelectedRows(prev => [...prev, params.row.id]);
            } else {
              setSelectedRows(prev => prev.filter(id => id !== params.row.id));
            }
          }}
          size="small"
        />
      ),
    },
    { field: 'createdDate', headerName: 'Entry Date', flex: 1, minWidth: 120, valueFormatter: (params) => formatDate(params.value) },
    { field: 'journalEntryID', headerName: 'Journal ID', flex: 1, minWidth: 100 },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value || '-'} arrow>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {params.value || '-'}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'debit',
      headerName: 'Debit',
      flex: 1,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <span style={{ color: params.value > 0 ? getDebitColor() : 'inherit', fontWeight: params.value > 0 ? 'bold' : 'normal' }}>
          {formatCurrency(params.value)}
        </span>
      ),
    },
    {
      field: 'credit',
      headerName: 'Credit',
      flex: 1,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <span style={{ color: params.value > 0 ? getCreditColor() : 'inherit', fontWeight: params.value > 0 ? 'bold' : 'normal' }}>
          {formatCurrency(params.value)}
        </span>
      ),
    },
    { field: 'branchName', headerName: 'Branch', flex: 1, minWidth: 120, valueFormatter: (params) => params.value || '-' },
    { field: 'applicationType', headerName: 'App Type', flex: 1, minWidth: 120, valueFormatter: (params) => params.value || '-' },
    {
      field: 'cancelled',
      headerName: 'Status',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Chip label={params.value ? "Inactive" : "Active"} color={params.value ? "error" : "success"} size="small" />
      ),
    },
    { field: 'createdBy', headerName: 'Created By', flex: 1, minWidth: 120, valueFormatter: (params) => params.value || '-' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth sx={{ '& .MuiDialog-paper': { height: '95vh' } }}>
      <DialogTitle sx={{ bgcolor: colors.blueAccent[700], color: '#fff', p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6" fontWeight="bold">Account Summary Details</Typography>
            <Typography variant="body2">
              {itemData?.accountName || itemData?.AccountName} ({itemData?.accountNumber || itemData?.AccountNumber})
            </Typography>
          </Box>
          <Button onClick={onClose} variant="outlined" size="small" startIcon={<CloseIcon />} sx={{ color: '#fff', borderColor: '#fff' }}>
            Close
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {!loading && !error && summaryData.length > 0 && (
          <Box flex={1} display="flex" flexDirection="column">
            {/* Top Controls: Search + Show Filters horizontally */}
            <Box p={2} borderBottom={`1px solid ${getBorderColor()}`} bgcolor={colors.primary[400]}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by description, journal ID, branch, app type, created by..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                      endAdornment: searchText && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchText("")}><ClearIcon /></IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ bgcolor: 'background.paper' }}
                  />
                </Grid>
                <Grid item xs={12} md={5} container justifyContent="flex-end" spacing={1}>
                  <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={<FilterListIcon />}
                      onClick={() => setShowFilters(!showFilters)}
                      endIcon={activeFilterCount > 0 && <Chip label={activeFilterCount} size="small" color="primary" />}
                    >
                      {showFilters ? "Hide Filters" : "Show Filters"}
                    </Button>
                  </Grid>
                  {(searchText || activeFilterCount > 0) && (
                    <Grid item>
                      <Button variant="outlined" color="error" startIcon={<ClearIcon />} onClick={clearAllFilters}>
                        Clear All
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* Collapsible Filters Panel */}
              {showFilters && (
                <Paper elevation={3} sx={{ mt: 2, p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Account Type</InputLabel>
                        <Select
                          value={filters.accountType}
                          label="Account Type"
                          onChange={(e) => setFilters(prev => ({ ...prev, accountType: e.target.value }))}
                        >
                          <MenuItem value="">All</MenuItem>
                          {uniqueAccountTypes.map(type => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Branch</InputLabel>
                        <Select
                          value={filters.branch}
                          label="Branch"
                          onChange={(e) => setFilters(prev => ({ ...prev, branch: e.target.value }))}
                        >
                          <MenuItem value="">All</MenuItem>
                          {uniqueBranches.map(branch => (
                            <MenuItem key={branch} value={branch}>{branch}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={filters.status ?? ""}
                          label="Status"
                          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="inactive">Inactive</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="From"
                        InputLabelProps={{ shrink: true }}
                        value={filters.dateFrom}
                        onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="To"
                        InputLabelProps={{ shrink: true }}
                        value={filters.dateTo}
                        onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Box>

            {/* Summary Cards */}
            <Box display="flex" gap={2} p={2} flexWrap="wrap" borderBottom={`1px solid ${getBorderColor()}`}>
              <Box p={1} borderRadius={1} bgcolor={`${getDebitColor()}20`} border={`1px solid ${getBorderColor()}`} minWidth={130}>
                <Typography variant="caption" color="text.secondary">Total Debit</Typography>
                <Typography variant="body1" fontWeight="bold" color={getDebitColor()}>
                  {formatCurrency(totals.totalDebit)}
                </Typography>
              </Box>
              <Box p={1} borderRadius={1} bgcolor={`${getCreditColor()}20`} border={`1px solid ${getBorderColor()}`} minWidth={130}>
                <Typography variant="caption" color="text.secondary">Total Credit</Typography>
                <Typography variant="body1" fontWeight="bold" color={getCreditColor()}>
                  {formatCurrency(totals.totalCredit)}
                </Typography>
              </Box>
              <Box p={1} borderRadius={1} bgcolor={colors.blueAccent?.[800] + '20'} border={`1px solid ${getBorderColor()}`} minWidth={130}>
                <Typography variant="caption" color="text.secondary">Balance</Typography>
                <Typography variant="body1" fontWeight="bold" color={totals.balance >= 0 ? getDebitColor() : getCreditColor()}>
                  {formatCurrency(totals.balance)}
                </Typography>
              </Box>
              <Box p={1} borderRadius={1} bgcolor={colors.primary?.[800] + '20'} border={`1px solid ${getBorderColor()}`} minWidth={130}>
                <Typography variant="caption" color="text.secondary">Showing</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {filteredData.length} of {summaryData.length}
                </Typography>
              </Box>
              {selectedRows.length > 0 && (
                <Box p={1} borderRadius={1} bgcolor={colors.orange?.[800] + '20'} border={`1px solid ${getBorderColor()}`} minWidth={130}>
                  <Typography variant="caption" color="text.secondary">Selected</Typography>
                  <Typography variant="body1" fontWeight="bold" color="orange">
                    {selectedRows.length}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* DataGrid with original header style */}
            <DataGrid
              rows={filteredData}
              columns={columns}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick={false}
              sx={{
                border: 0,
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: colors.blueAccent[700] || '#1976d2',
                  color: '#ffffff',
                  fontWeight: 'bold',
                  minHeight: '52px !important',
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                  },
                },
                '& .MuiDataGrid-columnSeparator': {
                  display: 'none',
                },
                '& .MuiDataGrid-row': {
                  minHeight: '52px !important',
                  '&:hover': {
                    backgroundColor: colors.primary[700] || '#f5f5f5',
                  },
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: `1px solid ${getBorderColor()}`,
                  padding: '0 16px',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-footerContainer': {
                  backgroundColor: colors.primary[400] || '#e3f2fd',
                  borderTop: `1px solid ${getBorderColor()}`,
                },
              }}
            />
          </Box>
        )}

        {!loading && !error && summaryData.length === 0 && (
          <Box display="flex" justifyContent="center" alignItems="center" height={200} color="text.secondary">
            <Typography variant="h6">No journal entries found for this account.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}


export function SummaryViewModal(props) {
  const { open, onClose, itemData } = props;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState([]);

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    accountType: "",
    branch: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  // Selected rows (single checkbox per row)
  const [selectedRows, setSelectedRows] = useState([]);

  const base_url = AccountsandFinanceurl.uri;

  useEffect(() => {
    if (open && itemData) {
      const accountId = itemData.id || itemData.accountID || itemData.AccountID;

      if (!accountId) {
        setError("Incomplete data. Cannot fetch Account Summary details.");
        setLoading(false);
        return;
      }

      const fetchDetails = async () => {
        setLoading(true);
        setError(null);

        const query = `
          query VwJournalLinesViewByColumnValues {
            vwJournalLinesViewByColumnValues(
                accountCategory: { identifierId: ${accountId} }
            ) {
                id
                journalEntryID
                accountName
                accountNumber
                accountSubCategoryName
                accountCategoryName
                debit
                credit
                description
                createdDate
                createdBy
                applicationType
                cancelled
                branchName
            }
          }
        `;

        try {
          const response = await graphqlQuery(query, base_url);

          let journalData = [];
          if (Array.isArray(response)) {
            journalData = response;
          } else if (response?.vwJournalLinesViewByColumnValues) {
            journalData = response.vwJournalLinesViewByColumnValues;
          }

          setSummaryData(Array.isArray(journalData) ? journalData : []);
        } catch (err) {
          setError(err.message || "An error occurred while fetching account summary details.");
        } finally {
          setLoading(false);
        }
      };

      fetchDetails();
    } else {
      setSummaryData([]);
      setSelectedRows([]);
      setSearchText("");
      setFilters({ accountType: "", branch: "", status: "", dateFrom: "", dateTo: "" });
    }
  }, [open, itemData]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '-';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getDebitColor = () => colors?.green?.[500] || '#4caf50';
  const getCreditColor = () => colors?.red?.[500] || '#f44336';
  const getBorderColor = () => colors?.grey?.[700] || '#e0e0e0';

  const uniqueBranches = useMemo(() => {
    const branches = [...new Set(summaryData.map(row => row.branchName).filter(Boolean))];
    return branches.sort();
  }, [summaryData]);

  const uniqueAccountTypes = useMemo(() => {
    const types = [...new Set(summaryData.map(row => row.accountSubCategoryName).filter(Boolean))];
    return types.sort();
  }, [summaryData]);

  const filteredData = useMemo(() => {
    let filtered = [...summaryData];

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(row =>
        String(row.description || "").toLowerCase().includes(search) ||
        String(row.journalEntryID || "").toLowerCase().includes(search) ||
        String(row.branchName || "").toLowerCase().includes(search) ||
        String(row.applicationType || "").toLowerCase().includes(search) ||
        String(row.createdBy || "").toLowerCase().includes(search)
      );
    }

    if (filters.accountType) filtered = filtered.filter(row => row.accountSubCategoryName === filters.accountType);
    if (filters.branch) filtered = filtered.filter(row => row.branchName === filters.branch);
    if (filters.status) {
      filtered = filtered.filter(row => {
        const isCancelled = row.cancelled === true;
        return filters.status === "active" ? !isCancelled : isCancelled;
      });
    }
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(row => {
        const rowDate = new Date(row.createdDate);
        rowDate.setHours(0, 0, 0, 0);
        return rowDate >= fromDate;
      });
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(row => {
        const rowDate = new Date(row.createdDate);
        rowDate.setHours(0, 0, 0, 0);
        return rowDate <= toDate;
      });
    }

    return filtered;
  }, [summaryData, searchText, filters]);

  const totals = useMemo(() => {
    return filteredData.reduce((acc, row) => {
      acc.totalDebit += Number(row.debit) || 0;
      acc.totalCredit += Number(row.credit) || 0;
      acc.balance += (Number(row.debit) || 0) - (Number(row.credit) || 0);
      return acc;
    }, { totalDebit: 0, totalCredit: 0, balance: 0 });
  }, [filteredData]);

  const activeFilterCount = useMemo(() => {
    return (searchText ? 1 : 0) + Object.values(filters).filter(v => v !== "").length;
  }, [searchText, filters]);

  const clearAllFilters = () => {
    setSearchText("");
    setFilters({ accountType: "", branch: "", status: "", dateFrom: "", dateTo: "" });
  };

  const columns = [
    // Single checkbox per row
    {
      field: 'selection',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Checkbox
          checked={selectedRows.includes(params.row.id)}
          onChange={(event) => {
            if (event.target.checked) {
              setSelectedRows(prev => [...prev, params.row.id]);
            } else {
              setSelectedRows(prev => prev.filter(id => id !== params.row.id));
            }
          }}
          size="small"
        />
      ),
    },
    { field: 'createdDate', headerName: 'Entry Date', flex: 1, minWidth: 120, valueFormatter: (params) => formatDate(params.value) },
    { field: 'journalEntryID', headerName: 'Journal ID', flex: 1, minWidth: 100 },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value || '-'} arrow>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {params.value || '-'}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'debit',
      headerName: 'Debit',
      flex: 1,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <span style={{ color: params.value > 0 ? getDebitColor() : 'inherit', fontWeight: params.value > 0 ? 'bold' : 'normal' }}>
          {formatCurrency(params.value)}
        </span>
      ),
    },
    {
      field: 'credit',
      headerName: 'Credit',
      flex: 1,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <span style={{ color: params.value > 0 ? getCreditColor() : 'inherit', fontWeight: params.value > 0 ? 'bold' : 'normal' }}>
          {formatCurrency(params.value)}
        </span>
      ),
    },
    { field: 'branchName', headerName: 'Branch', flex: 1, minWidth: 120, valueFormatter: (params) => params.value || '-' },
    { field: 'applicationType', headerName: 'App Type', flex: 1, minWidth: 120, valueFormatter: (params) => params.value || '-' },
    {
      field: 'cancelled',
      headerName: 'Status',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Chip label={params.value ? "Inactive" : "Active"} color={params.value ? "error" : "success"} size="small" />
      ),
    },
    { field: 'createdBy', headerName: 'Created By', flex: 1, minWidth: 120, valueFormatter: (params) => params.value || '-' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth sx={{ '& .MuiDialog-paper': { height: '95vh' } }}>
      <DialogTitle sx={{ bgcolor: colors.blueAccent[700], color: '#fff', p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6" fontWeight="bold">Account Summary Details</Typography>
            <Typography variant="body2">
              {itemData?.accountName || itemData?.AccountName} ({itemData?.accountNumber || itemData?.AccountNumber})
            </Typography>
          </Box>
          <Button onClick={onClose} variant="outlined" size="small" startIcon={<CloseIcon />} sx={{ color: '#fff', borderColor: '#fff' }}>
            Close
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {!loading && !error && summaryData.length > 0 && (
          <Box flex={1} display="flex" flexDirection="column">
            {/* Top Controls: Search + Show Filters horizontally */}
            <Box p={2} borderBottom={`1px solid ${getBorderColor()}`} bgcolor={colors.primary[400]}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by description, journal ID, branch, app type, created by..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                      endAdornment: searchText && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchText("")}><ClearIcon /></IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ bgcolor: 'background.paper' }}
                  />
                </Grid>
                <Grid item xs={12} md={5} container justifyContent="flex-end" spacing={1}>
                  <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={<FilterListIcon />}
                      onClick={() => setShowFilters(!showFilters)}
                      endIcon={activeFilterCount > 0 && <Chip label={activeFilterCount} size="small" color="primary" />}
                    >
                      {showFilters ? "Hide Filters" : "Show Filters"}
                    </Button>
                  </Grid>
                  {(searchText || activeFilterCount > 0) && (
                    <Grid item>
                      <Button variant="outlined" color="error" startIcon={<ClearIcon />} onClick={clearAllFilters}>
                        Clear All
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* Collapsible Filters Panel */}
              {showFilters && (
                <Paper elevation={3} sx={{ mt: 2, p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Account Type</InputLabel>
                        <Select
                          value={filters.accountType}
                          label="Account Type"
                          onChange={(e) => setFilters(prev => ({ ...prev, accountType: e.target.value }))}
                        >
                          <MenuItem value="">All</MenuItem>
                          {uniqueAccountTypes.map(type => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Branch</InputLabel>
                        <Select
                          value={filters.branch}
                          label="Branch"
                          onChange={(e) => setFilters(prev => ({ ...prev, branch: e.target.value }))}
                        >
                          <MenuItem value="">All</MenuItem>
                          {uniqueBranches.map(branch => (
                            <MenuItem key={branch} value={branch}>{branch}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={filters.status}
                          label="Status"
                          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="inactive">Inactive</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="From"
                        InputLabelProps={{ shrink: true }}
                        value={filters.dateFrom}
                        onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="To"
                        InputLabelProps={{ shrink: true }}
                        value={filters.dateTo}
                        onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Box>

            {/* Summary Cards */}
            <Box display="flex" gap={2} p={2} flexWrap="wrap" borderBottom={`1px solid ${getBorderColor()}`}>
              <Box p={1} borderRadius={1} bgcolor={`${getDebitColor()}20`} border={`1px solid ${getBorderColor()}`} minWidth={130}>
                <Typography variant="caption" color="text.secondary">Total Debit</Typography>
                <Typography variant="body1" fontWeight="bold" color={getDebitColor()}>
                  {formatCurrency(totals.totalDebit)}
                </Typography>
              </Box>
              <Box p={1} borderRadius={1} bgcolor={`${getCreditColor()}20`} border={`1px solid ${getBorderColor()}`} minWidth={130}>
                <Typography variant="caption" color="text.secondary">Total Credit</Typography>
                <Typography variant="body1" fontWeight="bold" color={getCreditColor()}>
                  {formatCurrency(totals.totalCredit)}
                </Typography>
              </Box>
              <Box p={1} borderRadius={1} bgcolor={colors.blueAccent?.[800] + '20'} border={`1px solid ${getBorderColor()}`} minWidth={130}>
                <Typography variant="caption" color="text.secondary">Balance</Typography>
                <Typography variant="body1" fontWeight="bold" color={totals.balance >= 0 ? getDebitColor() : getCreditColor()}>
                  {formatCurrency(totals.balance)}
                </Typography>
              </Box>
              <Box p={1} borderRadius={1} bgcolor={colors.primary?.[800] + '20'} border={`1px solid ${getBorderColor()}`} minWidth={130}>
                <Typography variant="caption" color="text.secondary">Showing</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {filteredData.length} of {summaryData.length}
                </Typography>
              </Box>
              {selectedRows.length > 0 && (
                <Box p={1} borderRadius={1} bgcolor={colors.orange?.[800] + '20'} border={`1px solid ${getBorderColor()}`} minWidth={130}>
                  <Typography variant="caption" color="text.secondary">Selected</Typography>
                  <Typography variant="body1" fontWeight="bold" color="orange">
                    {selectedRows.length}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* DataGrid with original header style */}
            <DataGrid
              rows={filteredData}
              columns={columns}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick={false}
              sx={{
                border: 0,
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: colors.blueAccent[700] || '#1976d2',
                  color: '#ffffff',
                  fontWeight: 'bold',
                  minHeight: '52px !important',
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                  },
                },
                '& .MuiDataGrid-columnSeparator': {
                  display: 'none',
                },
                '& .MuiDataGrid-row': {
                  minHeight: '52px !important',
                  '&:hover': {
                    backgroundColor: colors.primary[700] || '#f5f5f5',
                  },
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: `1px solid ${getBorderColor()}`,
                  padding: '0 16px',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-footerContainer': {
                  backgroundColor: colors.primary[400] || '#e3f2fd',
                  borderTop: `1px solid ${getBorderColor()}`,
                },
              }}
            />
          </Box>
        )}

        {!loading && !error && summaryData.length === 0 && (
          <Box display="flex" justifyContent="center" alignItems="center" height={200} color="text.secondary">
            <Typography variant="h6">No journal entries found for this account.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ReceiptViewModal(props) {
  const { open, onClose, itemData } = props;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [items, setItems] = useState([]);
  const [branchInfo, setBranchInfo] = useState({
    branchName: "",
    building: "",
    company: "",
    contacts: "",
    location: "",
    isWarehouse: false
  });

  const saleOrderId = itemData.saleOrderId;

  // Get decodedToken for user info
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);

  // Theme colors
  const theme = {
    primary: "#5DADE2",
    success: "#4CAF50",
    warning: "#FFA726",
    discount: "#27AE60",
    error: "#E74C3C",
  };

  useEffect(() => {
    if (open && itemData.saleOrderId) {
      fetchReceiptData(itemData.saleOrderId);
    } else {
      setReceiptData(null);
      setItems([]);
      setLoading(true);
      setError(null);
    }
  }, [open, itemData.saleOrderId]);

  // Fetch branch information
  const fetchBranchInfo = useCallback(async (branchId) => {
    const query = `
      query {
        branchByID(branch: { id: ${branchId} }) {
          branchName
          building
          company
          contacts
          location
          isWarehouse
        }
      }
    `;

    try {
      const result = await graphqlQuery(query, companyManagementUrl.uri);

      // The graphqlQuery returns the direct data object
      if (result && typeof result === 'object') {
        setBranchInfo({
          branchName: result.branchName || "",
          building: result.building || "",
          company: result.company || "",
          contacts: result.contacts || "",
          location: result.location || "",
          isWarehouse: Boolean(result.isWarehouse) || false
        });
      }
    } catch (error) {
      console.error("Error fetching branch info:", error);
      // Keep default values
    }
  }, []);

  const fetchReceiptData = useCallback(async (orderId) => {
    setLoading(true);
    setError(null);

    const query = `
      query SaleOrderItemViewById {
        saleOrderItemViewById(
            saleOrderItemView: { saleOrder: ${orderId} }
        ) {
            id
            saleOrder
            customerId
            customerName
            productId
            productName
            amount
            uomId
            unitofMeasure
            quantity
            companyId
            companyName
            createdBy
            updatedBy
            createdDate
            updatedDate
            cashier
            color
            size
            saleDate
            reversed
            branchId
        }
      }
    `;

    try {
      const response = await graphqlQuery(query, salesManagement.uri);

      let receiptItems = [];

      if (Array.isArray(response)) {
        receiptItems = response;
      } else if (response?.data?.saleOrderItemViewById) {
        receiptItems = response.data.saleOrderItemViewById;
      } else if (response?.saleOrderItemViewById) {
        receiptItems = response.saleOrderItemViewById;
      } else if (Array.isArray(response?.data)) {
        receiptItems = response.data;
      } else if (response && typeof response === 'object') {
        // If it's a single object, wrap it in an array
        receiptItems = [response];
      }


      if (Array.isArray(receiptItems) && receiptItems.length > 0) {
        // Group items by sale order and calculate totals
        const firstItem = receiptItems[0];

        // Fetch branch information if branchId exists
        if (firstItem.branchId) {
          fetchBranchInfo(firstItem.branchId);
        }

        // Calculate totals - amount might be total price or unit price
        // Based on typical structure, let's assume amount is total price for the line item
        const totalAmount = receiptItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
        const totalQuantity = receiptItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

        // Calculate unit price if needed
        const itemsWithUnitPrice = receiptItems.map(item => {
          const quantity = Number(item.quantity) || 1;
          const totalPrice = Number(item.amount) || 0;
          const unitPrice = quantity > 0 ? totalPrice / quantity : 0;

          return {
            id: item.id,
            saleOrderId: item.saleOrder,
            productId: item.productId,
            productName: item.productName || "Unknown Product",
            quantity: item.quantity,
            unitPrice: unitPrice,
            totalPrice: totalPrice,
            unitofMeasure: item.unitofMeasure || "Pcs",
            color: item.color,
            size: item.size,
            reversed: item.reversed || false,
            createdBy: item.createdBy,
            createdDate: item.createdDate,
            cashier: item.cashier,
            saleDate: item.saleDate,
            branchId: item.branchId
          };
        });

        // Create receipt data structure
        const receipt = {
          saleOrderId: firstItem.saleOrder || orderId,
          customerId: firstItem.customerId,
          customerName: firstItem.customerName || "Cash Customer",
          companyName: firstItem.companyName || "Company",
          cashier: firstItem.cashier || "Cashier",
          saleDate: firstItem.saleDate || firstItem.createdDate || new Date().toISOString(),
          totalAmount: totalAmount,
          totalQuantity: totalQuantity,
          items: itemsWithUnitPrice,
          branchId: firstItem.branchId
        };

        setReceiptData(receipt);
        setItems(itemsWithUnitPrice);
      } else {
        setError("No receipt data found for this sale order.");
      }
    } catch (err) {
      setError(err.message || "An error occurred while fetching receipt data.");
      console.error("Receipt API Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return '-';
    }
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'KSh 0.00';
    return `KSh ${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)}`;
  };

  // Handle printing receipt
  const handlePrintReceipt = () => {
    if (!receiptData) return;

    const receiptWindow = window.open("", "PRINT", "height=600,width=500");
    const date = new Date().toLocaleString();

    const receiptContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          @media print {
            @page {
              size: auto;
              margin: 5mm;
            }
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              margin: 10px;
              font-size: 11px;
            }
            .no-print {
              display: none !important;
            }
          }
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            margin: 15px;
          }
          .header {
            text-align: center;
            margin-bottom: 15px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }
          .footer {
            text-align: center;
            margin-top: 20px;
            padding-top: 10px;
            border-top: 1px dashed #000;
          }
          .total {
            font-weight: bold;
            font-size: 14px;
          }
          .item-grid {
            display: grid;
            grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
            gap: 5px;
          }
          .item-header {
            font-weight: bold;
            border-bottom: 2px solid #333;
            padding-bottom: 5px;
            margin-bottom: 10px;
          }
          .item-row {
            padding: 4px 0;
            border-bottom: 1px solid #eee;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h2 style="margin: 0;">${receiptData.companyName}</h2>
          <h3 style="margin: 5px 0 10px 0;">SALE RECEIPT</h3>
          ${branchInfo.branchName ? `
            <div style="font-size: 12px; margin-bottom: 5px;">
              <strong>Branch:</strong> ${branchInfo.branchName}
            </div>
          ` : ''}
          ${branchInfo.location || branchInfo.building ? `
            <div style="font-size: 11px; margin-bottom: 5px;">
              <strong>Location:</strong> ${branchInfo.location || ''} ${branchInfo.building || ''}
            </div>
          ` : ''}
          ${branchInfo.contacts ? `
            <div style="font-size: 11px; margin-bottom: 5px;">
              <strong>Contact:</strong> ${branchInfo.contacts}
            </div>
          ` : ''}
          <div style="font-size: 12px;">
            <p style="margin: 2px 0;"><strong>Date:</strong> ${formatDate(receiptData.saleDate)}</p>
            <p style="margin: 2px 0;"><strong>Order ID:</strong> ${receiptData.saleOrderId}</p>
            <p style="margin: 2px 0;"><strong>Cashier:</strong> ${receiptData.cashier}</p>
            <p style="margin: 2px 0;"><strong>Customer:</strong> ${receiptData.customerName}</p>
          </div>
        </div>
        
        <div style="margin: 15px 0;">
          <div class="item-grid item-header">
            <span>Item</span>
            <span style="text-align: center;">Qty</span>
            <span style="text-align: center;">Unit</span>
            <span style="text-align: center;">Size/Color</span>
            <span style="text-align: right;">Amount</span>
          </div>
          
          ${receiptData.items.map(item => `
            <div class="item-grid item-row">
              <span>${item.productName}</span>
              <span style="text-align: center;">${item.quantity}</span>
              <span style="text-align: center;">${item.unitofMeasure}</span>
              <span style="text-align: center; font-size: 10px;">
                ${item.size || '-'}/${item.color || '-'}
              </span>
              <span style="text-align: right;">${formatCurrency(item.totalPrice)}</span>
            </div>
          `).join('')}
        </div>
        
        <div style="border-top: 2px solid #000; padding-top: 10px; margin-top: 15px;">
          <div style="display: flex; justify-content: space-between; margin: 5px 0; font-size: 13px;">
            <strong>Total Items:</strong>
            <strong>${receiptData.totalQuantity}</strong>
          </div>
          <div style="display: flex; justify-content: space-between; margin: 5px 0; font-size: 16px;">
            <strong>TOTAL AMOUNT:</strong>
            <strong>${formatCurrency(receiptData.totalAmount)}</strong>
          </div>
        </div>
        
        <div class="footer">
          <p style="margin: 5px 0;"><strong>Thank you for your purchase!</strong></p>
          ${branchInfo.location || branchInfo.contacts ? `
            <p style="margin: 5px 0; font-size: 10px;">
              ${branchInfo.location || ''} ${branchInfo.location && branchInfo.contacts ? '|' : ''} ${branchInfo.contacts || ''}
            </p>
          ` : ''}
          <p style="margin: 5px 0; font-size: 10px;">Printed: ${date}</p>
        </div>
      </body>
      </html>
    `;

    receiptWindow.document.write(receiptContent);
    receiptWindow.document.close();

    setTimeout(() => {
      receiptWindow.focus();
      receiptWindow.print();
      setTimeout(() => receiptWindow.close(), 1000);
    }, 500);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: theme.primary, color: "white" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ReceiptIcon />
            <Typography variant="h6">Receipt Details - Order #{saleOrderId}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && receiptData && (
          <>
            {/* Receipt Header */}
            <Paper sx={{ p: 3, mb: 3, border: `2px solid ${theme.primary}`, borderRadius: 2 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h4" fontWeight="bold" color={theme.primary}>
                  {receiptData.companyName}
                </Typography>
                <Typography variant="h6">SALE RECEIPT</Typography>
                {branchInfo.branchName && (
                  <Typography variant="subtitle1" color="text.secondary">
                    Branch: {branchInfo.branchName}
                  </Typography>
                )}
              </Box>

              {/* Branch Information */}
              {(branchInfo.location || branchInfo.building || branchInfo.contacts) && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    border: `1px solid ${theme.primary}`
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    <StoreIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Branch Details
                  </Typography>
                  <Grid container spacing={1}>
                    {branchInfo.location && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Location:</strong> {branchInfo.location}
                        </Typography>
                      </Grid>
                    )}
                    {branchInfo.building && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Building:</strong> {branchInfo.building}
                        </Typography>
                      </Grid>
                    )}
                    {branchInfo.contacts && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Contact:</strong> {branchInfo.contacts}
                        </Typography>
                      </Grid>
                    )}
                    {branchInfo.isWarehouse && (
                      <Grid item xs={12} sm={6}>
                        <Chip
                          label="Warehouse"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                    <CalendarTodayIcon fontSize="small" color="action" />
                    <Typography variant="body2"><strong>Sale Date:</strong></Typography>
                  </Box>
                  <Typography variant="body1" sx={{ ml: 4 }}>
                    {formatDate(receiptData.saleDate)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                    <ReceiptIcon fontSize="small" color="action" />
                    <Typography variant="body2"><strong>Order ID:</strong></Typography>
                  </Box>
                  <Typography variant="body1" sx={{ ml: 4 }} fontWeight="bold">
                    {receiptData.saleOrderId}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2"><strong>Customer:</strong></Typography>
                  </Box>
                  <Typography variant="body1" sx={{ ml: 4 }}>
                    {receiptData.customerName}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                    <StoreIcon fontSize="small" color="action" />
                    <Typography variant="body2"><strong>Cashier:</strong></Typography>
                  </Box>
                  <Typography variant="body1" sx={{ ml: 4 }}>
                    {receiptData.cashier}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Items List */}
            <Paper sx={{ p: 3, mb: 3, border: `1px solid #e0e0e0`, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCartIcon />
                Items Purchased ({receiptData.totalQuantity})
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {/* Header */}
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr 1fr',
                  gap: 1,
                  p: 2,
                  bgcolor: '#f5f5f5',
                  borderRadius: 1,
                  mb: 1,
                  fontWeight: 'bold'
                }}>
                  <Typography>Product</Typography>
                  <Typography textAlign="center">Qty</Typography>
                  <Typography textAlign="center">Unit</Typography>
                  <Typography textAlign="center">Size</Typography>
                  <Typography textAlign="center">Color</Typography>
                  <Typography textAlign="right">Amount</Typography>
                </Box>

                {/* Items */}
                {receiptData.items.map((item, index) => (
                  <Box
                    key={item.id || index}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr 1fr',
                      gap: 1,
                      p: 2,
                      borderBottom: '1px solid #eee',
                      alignItems: 'center',
                      bgcolor: item.reversed ? '#ffebee' : 'transparent'
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {item.productName}
                      </Typography>
                      {item.reversed && (
                        <Chip
                          label="REVERSED"
                          size="small"
                          color="error"
                          sx={{ height: 20, fontSize: 10, mt: 0.5 }}
                        />
                      )}
                    </Box>
                    <Typography textAlign="center">{item.quantity}</Typography>
                    <Typography textAlign="center">{item.unitofMeasure}</Typography>
                    <Typography textAlign="center">{item.size || '-'}</Typography>
                    <Typography textAlign="center">{item.color || '-'}</Typography>
                    <Typography textAlign="right" fontWeight="bold">
                      {formatCurrency(item.totalPrice)}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Summary */}
              <Box sx={{
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 1,
                border: `2px solid ${theme.primary}`
              }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Total Items:</Typography>
                    <Typography variant="h6">{receiptData.totalQuantity}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary" textAlign="right">Total Amount:</Typography>
                    <Typography variant="h4" textAlign="right" color={theme.primary} fontWeight="bold">
                      {formatCurrency(receiptData.totalAmount)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Additional Information */}
            <Paper sx={{ p: 2, border: `1px solid #e0e0e0`, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Transaction Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" display="block">Created By:</Typography>
                  <Typography variant="body2">{receiptData.items[0]?.cashier || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" display="block" textAlign="right">
                    Creation Date:
                  </Typography>
                  <Typography variant="body2" textAlign="right">
                    {formatDate(receiptData.items[0]?.createdDate)}
                  </Typography>
                </Grid>
                {branchInfo.branchName && (
                  <Grid item xs={12}>
                    <Typography variant="caption" display="block">Branch:</Typography>
                    <Typography variant="body2">
                      {branchInfo.branchName}
                      {branchInfo.location && ` (${branchInfo.location})`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </>
        )}

        {!loading && !error && !receiptData && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ReceiptIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No receipt data available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please select a sale order to view receipt details
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          color="inherit"
          startIcon={<CloseIcon />}
        >
          Close
        </Button>
        {receiptData && (
          <Button
            onClick={handlePrintReceipt}
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
          >
            Print Receipt
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}





const COLUMN_COUNT = 6;

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "-";

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const getInitials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";

// ---------- Small presentational pieces ----------
function StatCard({ label, value, hint, color = "primary", compact = false }) {
  return (
    <Box
      sx={(t) => ({
        flex: 1,
        minWidth: 150,
        p: 2,
        borderRadius: 2.5,
        border: `1px solid ${alpha(t.palette[color].main, 0.25)}`,
        backgroundColor: alpha(t.palette[color].main, 0.07),
        transition: "all .2s ease",
      })}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Typography
        variant={compact ? "h6" : "h4"}
        sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.5, fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

function ColorCell({ name }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Box
        sx={(t) => ({
          width: 14,
          height: 14,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: name || "transparent", // ignored by the browser if not a CSS colour
          border: `1px solid ${t.palette.divider}`,
          boxShadow: `0 0 0 2px ${alpha(t.palette.background.paper, 1)}`,
        })}
      />
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {name || "-"}
      </Typography>
    </Stack>
  );
}

/**
 * Props:
 *  - open, onClose, itemData
 *  - currentUser (optional, kept for compatibility)
 *  - onUpdated?: () => void  -> lets the parent refresh its grid after a save
 */
export function RetailDistributionViewModal(props) {
  const { open, onClose, itemData, currentUser, onUpdated } = props;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const base_url = inventoryManagementUrl.uri;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [distributionData, setDistributionData] = useState([]);

  const [isEditing, setIsEditing] = useState(false);
  // draft = { [rowId]: "string value typed in the input" }
  const [draft, setDraft] = useState({});

  // ---------- Fetch ----------
  const fetchDetails = useCallback(async () => {
    if (!itemData?.productCode || !itemData?.branchId || !itemData?.productId) {
      setError("Incomplete data. Cannot fetch distribution details.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const query = `
      query {
        retailInventoryByColumnValues(inventory: {
          productCode: "${itemData.productCode}",
          branchId: ${Number(itemData.branchId)},
          productId: ${Number(itemData.productId)}
        }) {
          id
          productCode
          productName
          productId
          colorId
          colorName
          size
          quantity
          branchId
          stockDueDate
          companyId
          updatedByName
          updatedDate
        }
      }
    `;

    try {
      const response = await graphqlQuery(query, base_url);
      const rows = Array.isArray(response)
        ? response
        : response?.retailInventoryByColumnValues ?? [];
      setDistributionData(rows);
    } catch (err) {
      setError(err.message || "An error occurred while fetching details.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [itemData, base_url]);

  useEffect(() => {
    if (open && itemData) {
      setIsEditing(false);
      setDraft({});
      setSuccess(null);
      fetchDetails();
    }
  }, [open, itemData, fetchDetails]);

  // ---------- Totals / validation ----------
  // The total as it existed BEFORE editing. Edits must always sum back to this.
  const originalTotal = useMemo(
    () => distributionData.reduce((sum, r) => sum + Number(r.quantity || 0), 0),
    [distributionData]
  );

  const parsedDraft = useMemo(() => {
    const result = {};
    distributionData.forEach((r) => {
      const raw = draft[r.id] ?? String(r.quantity ?? 0);
      const n = Number(raw);
      result[r.id] = {
        raw,
        value: n,
        valid: raw !== "" && Number.isInteger(n) && n >= 0,
      };
    });
    return result;
  }, [draft, distributionData]);

  const draftTotal = useMemo(
    () =>
      distributionData.reduce(
        (sum, r) => sum + (parsedDraft[r.id]?.valid ? parsedDraft[r.id].value : 0),
        0
      ),
    [parsedDraft, distributionData]
  );

  const allValid = distributionData.every((r) => parsedDraft[r.id]?.valid);
  const difference = draftTotal - originalTotal; // >0 = too many, <0 = too few
  const totalsMatch = difference === 0;
  const hasChanges = distributionData.some(
    (r) => parsedDraft[r.id]?.valid && parsedDraft[r.id].value !== Number(r.quantity)
  );
  const canSave = isEditing && allValid && totalsMatch && hasChanges && !saving;

  // Most recent edit across all rows (for the summary card)
  const lastUpdate = useMemo(() => {
    const withDates = distributionData.filter((r) => r.updatedDate);
    if (withDates.length === 0) return null;
    return withDates.reduce((latest, r) =>
      new Date(r.updatedDate) > new Date(latest.updatedDate) ? r : latest
    );
  }, [distributionData]);

  // Balance indicator while editing
  const balanceColor = !allValid ? "warning" : totalsMatch ? "success" : difference > 0 ? "error" : "warning";
  const balanceProgress = originalTotal > 0 ? Math.min(100, (draftTotal / originalTotal) * 100) : 0;
  const balanceMessage = !allValid
    ? "Quantities must be whole numbers (0 or more)."
    : totalsMatch
    ? `Total matches the original quantity (${originalTotal}).`
    : `The total must stay at ${originalTotal}. Current total is ${draftTotal} (${
        difference > 0 ? `${difference} over` : `${Math.abs(difference)} short`
      }).`;

  // ---------- Handlers ----------
  const startEdit = () => {
    const initial = {};
    distributionData.forEach((r) => (initial[r.id] = String(r.quantity ?? 0)));
    setDraft(initial);
    setSuccess(null);
    setError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setDraft({});
    setError(null);
    setIsEditing(false);
  };

  const handleChange = (id, value) => {
    // allow only digits (blank allowed while typing; flagged invalid)
    if (/^\d*$/.test(value)) {
      setDraft((prev) => ({ ...prev, [id]: value }));
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    // Only changed rows are sent, as a JSON string (matches the backend's ItemsJson).
    const changedItems = distributionData
      .filter((r) => parsedDraft[r.id].value !== Number(r.quantity))
      .map((r) => ({ id: Number(r.id), quantity: parsedDraft[r.id].value }));

    // The backend sets updatedBy and companyId from the logged-in user.
    // JSON.stringify twice: once to build the JSON, once to make it a GraphQL string literal.
    const mutation = `
      mutation {
        updateRetailDistribution(input: {
          productCode: ${JSON.stringify(String(itemData.productCode))},
          branchId: ${Number(itemData.branchId)},
          productId: ${Number(itemData.productId)},
          expectedTotal: ${originalTotal},
          itemsJson: ${JSON.stringify(JSON.stringify(changedItems))}
        }) {
          productCode
          expectedTotal
        }
      }
    `;

    try {
      await graphqlQuery(mutation, salesManagement.uri);
      setSuccess("Distribution updated successfully.");
      setIsEditing(false);
      setDraft({});
      await fetchDetails(); // reload so the new "original total" is the saved one
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(err.message || "An error occurred while saving.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // ---------- Render ----------
  const showContent = !loading && (!error || distributionData.length > 0);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundImage: "none",
          boxShadow: "0 24px 64px rgba(0,0,0,0.28)",
          overflow: "hidden",
        },
      }}
    >
      {/* ---------- Header ---------- */}
      <DialogTitle
        component="div"
        sx={(t) => ({
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${t.palette.divider}`,
          backgroundImage: `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.12)}, ${alpha(
            t.palette.primary.main,
            0
          )} 70%)`,
        })}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            variant="rounded"
            sx={(t) => ({
              width: 46,
              height: 46,
              borderRadius: 2,
              color: t.palette.primary.main,
              backgroundColor: alpha(t.palette.primary.main, 0.15),
            })}
          >
            <Inventory2OutlinedIcon />
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                Distribution Details
              </Typography>
              {isEditing && (
                <Chip label="Editing" size="small" color="warning" sx={{ fontWeight: 600 }} />
              )}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
              <Typography variant="body2" color="text.secondary" noWrap>
                {itemData?.productName}
              </Typography>
              {itemData?.productCode && (
                <Chip
                  label={itemData.productCode}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                />
              )}
            </Stack>
          </Box>

          <Tooltip title="Close">
            <span>
              <IconButton onClick={onClose} disabled={saving} size="small">
                <CloseRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </DialogTitle>

      {/* ---------- Content ---------- */}
      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        {loading && (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rounded" height={84} sx={{ flex: 1, borderRadius: 2.5 }} />
              ))}
            </Stack>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={42} />
            ))}
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {success}
          </Alert>
        )}

        {/* Summary cards */}
        {!loading && distributionData.length > 0 && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2.5 }}>
            <StatCard
              label={isEditing ? "Allocated" : "Total units"}
              value={isEditing ? `${draftTotal} / ${originalTotal}` : originalTotal}
              hint={isEditing ? "new / original" : "across all colors & sizes"}
              color={isEditing ? balanceColor : "primary"}
            />
            <StatCard
              label="Variants"
              value={distributionData.length}
              hint="color / size lines"
              color="info"
            />
            <StatCard
              label="Last updated"
              value={lastUpdate ? formatDate(lastUpdate.updatedDate) : "-"}
              hint={lastUpdate?.updatedByName ? `by ${lastUpdate.updatedByName}` : "no edits yet"}
              color="success"
              compact
            />
          </Stack>
        )}

        {/* Balance indicator (edit mode) */}
        {isEditing && showContent && (
          <Box
            sx={(t) => ({
              mb: 2.5,
              p: 2,
              borderRadius: 2.5,
              border: `1px solid ${alpha(t.palette[balanceColor].main, 0.4)}`,
              backgroundColor: alpha(t.palette[balanceColor].main, 0.08),
            })}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
              {balanceColor === "success" ? (
                <CheckCircleRoundedIcon color="success" fontSize="small" />
              ) : (
                <WarningAmberRoundedIcon color={balanceColor} fontSize="small" />
              )}
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {balanceMessage}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={balanceProgress}
              color={balanceColor}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
              <Typography variant="caption" color="text.secondary">
                {draftTotal} allocated
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {originalTotal} required
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Table */}
        {showContent && (
          <TableContainer
            sx={(t) => ({
              maxHeight: 380,
              borderRadius: 2.5,
              border: `1px solid ${t.palette.divider}`,
            })}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {["Color", "Size", "Quantity", "Stock Due Date", "Updated By", "Updated At"].map(
                    (label, i) => (
                      <TableCell
                        key={label}
                        align={i === 2 ? "right" : "left"}
                        sx={(t) => ({
                          py: 1.25,
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                          color: t.palette.text.secondary,
                          backgroundColor: t.palette.background.paper,
                          backgroundImage: `linear-gradient(${alpha(t.palette.text.primary, 0.05)}, ${alpha(
                            t.palette.text.primary,
                            0.05
                          )})`,
                        })}
                      >
                        {label}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {distributionData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 6, border: 0 }}>
                      <Inventory2OutlinedIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No distribution details found for this item.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  distributionData.map((row) => {
                    const p = parsedDraft[row.id];
                    const delta = p?.valid ? p.value - Number(row.quantity || 0) : 0;
                    const changed = isEditing && delta !== 0;

                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={(t) => ({
                          transition: "background-color .15s ease",
                          backgroundColor: changed ? alpha(t.palette.primary.main, 0.08) : "transparent",
                          "&:last-child td": { borderBottom: 0 },
                        })}
                      >
                        <TableCell>
                          <ColorCell name={row.colorName} />
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={row.size ?? "-"}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600, minWidth: 40 }}
                          />
                        </TableCell>

                        <TableCell align="right">
                          {isEditing ? (
                            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                              {changed && (
                                <Chip
                                  label={`${delta > 0 ? "+" : ""}${delta}`}
                                  size="small"
                                  color={delta > 0 ? "success" : "warning"}
                                  variant="outlined"
                                  sx={{ height: 22, fontWeight: 700, fontSize: 11 }}
                                />
                              )}
                              <TextField
                                size="small"
                                value={p?.raw ?? ""}
                                onChange={(e) => handleChange(row.id, e.target.value)}
                                error={!p?.valid}
                                disabled={saving}
                                inputProps={{
                                  inputMode: "numeric",
                                  style: {
                                    textAlign: "right",
                                    width: 64,
                                    fontWeight: 600,
                                    fontVariantNumeric: "tabular-nums",
                                  },
                                }}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                              />
                            </Stack>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                            >
                              {row.quantity}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(row.stockDueDate)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {row.updatedByName ? (
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Avatar
                                sx={(t) => ({
                                  width: 24,
                                  height: 24,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: t.palette.primary.main,
                                  backgroundColor: alpha(t.palette.primary.main, 0.15),
                                })}
                              >
                                {getInitials(row.updatedByName)}
                              </Avatar>
                              <Typography variant="body2" noWrap>
                                {row.updatedByName}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(row.updatedDate)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      {/* ---------- Footer ---------- */}
      <DialogActions
        sx={(t) => ({
          px: 3,
          py: 2,
          mt: 1,
          borderTop: `1px solid ${t.palette.divider}`,
          gap: 1,
        })}
      >
        {isEditing ? (
          <>
            <Button
              onClick={cancelEdit}
              disabled={saving}
              color="inherit"
              sx={{ textTransform: "none", borderRadius: 2, px: 2.5 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canSave}
              variant="contained"
              color="primary"
              disableElevation
              startIcon={
                saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />
              }
              sx={{ textTransform: "none", borderRadius: 2, px: 3, fontWeight: 600 }}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={onClose}
              color="inherit"
              sx={{ textTransform: "none", borderRadius: 2, px: 2.5 }}
            >
              Close
            </Button>
            <Button
              onClick={startEdit}
              disabled={loading || distributionData.length === 0}
              variant="contained"
              color="secondary"
              disableElevation
              startIcon={<EditOutlinedIcon />}
              sx={{ textTransform: "none", borderRadius: 2, px: 3, fontWeight: 600 }}
            >
              Edit Quantities
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
// export function RetailDistributionViewModal(props) {
//   const { open, onClose, itemData } = props;
//   const theme = useTheme();
//   const colors = tokens(theme.palette.mode);

//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [distributionData, setDistributionData] = useState([]);

//   const base_url = inventoryManagementUrl.uri;

//   useEffect(() => {
//     if (open && itemData) {
//       // Ensure we have the required data to make the query
//       if (!itemData.productCode || !itemData.branchId || !itemData.productId) {
//         setError("Incomplete data. Cannot fetch distribution details.");
//         setLoading(false);
//         return;
//       }

//       const fetchDetails = async () => {
//         setLoading(true);
//         setError(null);

//         // This query is based on the one you provided in the prompt
//         const query = `
//           query {
//           retailInventoryByColumnValues(inventory:{ productCode: "${itemData.productCode}", branchId: ${itemData.branchId}, productId: ${itemData.productId} }) {
//             id
//             productCode
//             productName
//             productId
//             colorId
//             colorName
//             size
//             quantity
//             branchId
//             stockDueDate  
//             companyId
          
            
//           }
//         }
//         `;

//         try {
//           const response = await graphqlQuery(query, base_url);
//           if (response) {
//             setDistributionData(response);
//           } else {
//             setDistributionData([]);
//           }
//         } catch (err) {
//           setError(err.message || "An error occurred while fetching details.");
//           console.error(err);
//         } finally {
//           setLoading(false);
//         }
//       };

//       fetchDetails();
//     }
//   }, [open, itemData]);

//   return (
//     <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
//       <DialogTitle
//         sx={{
//           color: colors.grey[100],
//           borderBottom: `1px solid ${colors.grey[700]}`,
//         }}
//       >
//         Distribution Details
//         <Typography variant="body2" color="text.secondary">
//           {itemData?.productName} ({itemData?.productCode})
//         </Typography>
//       </DialogTitle>
//       <DialogContent>
//         {loading && (
//           <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
//             <CircularProgress />
//           </Box>
//         )}
//         {error && (
//           <Alert severity="error" sx={{ my: 2 }}>
//             {error}
//           </Alert>
//         )}
//         {!loading && !error && (
//           <TableContainer component={Paper} sx={{ mt: 2 }}>
//             <Table stickyHeader>
//               <TableHead>
//                 <TableRow>
//                   <TableCell>Color</TableCell>
//                   <TableCell>Size</TableCell>
//                   <TableCell>Quantity</TableCell>
//                   <TableCell>StockDue Date</TableCell>
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {distributionData.length === 0 ? (
//                   <TableRow>
//                     <TableCell colSpan={7} align="center">
//                       No distribution details found for this item.
//                     </TableCell>
//                   </TableRow>
//                 ) : (
//                   distributionData.map((row) => (
//                     <TableRow key={row.id}>
//                       <TableCell>{row.colorName}</TableCell>
//                       <TableCell>{row.size}</TableCell>
//                       <TableCell>{row.quantity}</TableCell>
//                       <TableCell>{row.stockDueDate ? new Date(row.stockDueDate).toLocaleDateString() : '-'}</TableCell>
//                     </TableRow>
//                   ))
//                 )}
//               </TableBody>
//             </Table>
//           </TableContainer>
//         )}
//       </DialogContent>
//       <DialogActions>
//         <Button onClick={onClose} color="primary" variant="outlined">
//           Close
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );
// }
//End of new component

export function CommunicationComponent(props) {
  const { communicationType } = props; // email or sms
  const base_url = emailService.uri;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const validationSchema = yup.object().shape({
    subjectTitle:
      communicationType === "email"
        ? yup.string().required("Subject is required")
        : yup.string(),
    recipientEmail:
      communicationType === "email"
        ? yup
          .string()
          .email("Invalid email format")
          .required("Recipient email is required")
        : yup.string(),
    recipientNumber:
      communicationType === "sms"
        ? yup
          .string()
          .matches(/^[0-9]+$/, "Phone number must be digits only")
          .required("Recipient number is required")
        : yup.string(),
    body: yup.string().required("Body is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const mutationData =
        communicationType === "email"
          ? {
            subjectTitle: values.subjectTitle,
            recipientEmail: values.recipientEmail,
            body: values.body,
            messageId: 1,
          }
          : {
            phoneNumber: values.recipientNumber,
            body: values.body,
            messageId: 1,
          };
      const response = await generateAndExecuteMutation(
        communicationType === "email" ? "createEmailMessage" : "createMessage",
        mutationData,
        communicationType === "email" ? "email" : "smsmessage",
        communicationType === "email" ? base_url : smsservice.uri
      );

      if (Object.values(response)[0] != null) {
        props.onClose();
        //props.onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `${communicationType.charAt(0).toUpperCase() +
            communicationType.slice(1)
            } has been sent successfully.`,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
    setSubmitting(false);
    props.onClose();
  };

  return (
    <React.Fragment>
      <Dialog open={props.open} onClose={props.onClose}>
        <DialogTitle sx={{ color: colors.grey[100] }}>
          {communicationType === "email" ? "Send Email" : "Send SMS"}
        </DialogTitle>
        <Formik
          initialValues={{
            subjectTitle: "",
            recipientEmail: "",
            recipientNumber: "",
            body: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <DialogContentText sx={{ color: colors.greenAccent[400] }}>
                  Fill in the details to send a {communicationType}.
                </DialogContentText>
                {communicationType === "email" && (
                  <>
                    <TextField
                      fullWidth
                      label="Subject Title"
                      name="subjectTitle"
                      variant="filled"
                      value={values.subjectTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.subjectTitle && !!errors.subjectTitle}
                      helperText={touched.subjectTitle && errors.subjectTitle}
                      margin="normal"
                    />
                    <TextField
                      fullWidth
                      label="Recipient Email"
                      name="recipientEmail"
                      variant="filled"
                      value={values.recipientEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.recipientEmail && !!errors.recipientEmail}
                      helperText={
                        touched.recipientEmail && errors.recipientEmail
                      }
                      margin="normal"
                    />
                  </>
                )}
                {communicationType === "sms" && (
                  <TextField
                    fullWidth
                    label="Recipient Number"
                    name="recipientNumber"
                    variant="filled"
                    value={values.recipientNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.recipientNumber && !!errors.recipientNumber}
                    helperText={
                      touched.recipientNumber && errors.recipientNumber
                    }
                    margin="normal"
                  />
                )}
                <TextField
                  fullWidth
                  label="Body"
                  name="body"
                  variant="filled"
                  value={values.body}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.body && !!errors.body}
                  helperText={touched.body && errors.body}
                  margin="normal"
                  multiline
                  rows={4}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  color="error"
                  variant="outlined"
                  onClick={props.onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  type="submit"
                  disabled={isSubmitting}
                  color="success"
                >
                  {isSubmitting
                    ? "Sending..."
                    : `Send ${communicationType.charAt(0).toUpperCase() +
                    communicationType.slice(1)
                    }`}
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>
    </React.Fragment>
  );
}

export function DocumentAccessModal(props) {
  const [documentAccess, setDocumentAccess] = React.useState([]);
  const [documentById, setDocumentById] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const documentUrl = documentManagementUrl.uri;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const cleanJsonString = (jsonString) => {
    const parsedData = JSON.parse(jsonString);
    const cleanUsers = parsedData.users.map((user) => ({
      ...user,
      permissions: JSON.parse(user.permissions),
    }));
    return { ...parsedData, users: cleanUsers };
  };
  const cleanJson = (jsonString) => {
    const parsedData = JSON.parse(jsonString);
    const cleanUsers = parsedData.Users.map((user) => ({
      ...user,
      permissions: JSON.parse(user.permissions),
    }));
    return { ...parsedData, users: cleanUsers };
  };
  //  useEffect(() => {
  //    async function fetchAllData() {
  //      try {
  //        const returnFields = ["id", "processWorkFlow"];
  //        const inputFields = { id: caseId };
  //        const Facts = await fetchDataEngine(
  //          "userById",
  //          null,
  //          inputFields,
  //          returnFields,
  //          base_url
  //        );
  //        setCaseData(Facts);
  //      } catch (error) {
  //        console.error("Error fetching data:", error);
  //      } finally {
  //        setLoading(false);
  //      }
  //    }
  //    fetchAllData();
  //  }, []);
  const fetchDocumentData = async () => {
    const documentId = props.data.id;
    try {
      const data = await getDocumentAccessPermissions(documentId, documentUrl);
      const cleanedData = cleanJson(data);
      setDocumentAccess(cleanedData.users);

      const dataById = await getDocumentAccessPermissionsById(
        documentId,
        documentUrl
      );
      const cleanedDataById = cleanJsonString(dataById);
      setDocumentById(cleanedDataById.users);
    } catch (error) {
      console.error("Error fetching document data:", error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchDocumentData();
  }, []);

  const validationSchema = yup.object().shape({
    permissions: yup.object().required("Permissions are required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const mutationData = Object.keys(values.permissions).map((userId) => ({
        documentId: props.data.id,
        userId: parseInt(userId),
        readRight: values.permissions[userId].ReadRight,
        downloadRight: values.permissions[userId].DownloadRight,
        editRight: values.permissions[userId].EditRight,
        deleteRight: values.permissions[userId].DeleteRight,
        companyId: 6,
        active: false,
        company: null,
      }));

      const response = await generateAndExecuteBulkMutation(
        "createAccessBulk",
        "accessList",
        mutationData,

        documentManagementUrl.uri
      );

      if (Object.values(response)[0] != null) {
        props.onClose();
        props.onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Document rights have been assigned successfully.`,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
    setSubmitting(false);
    props.onClose();
  };

  const initialValues = {
    permissions: documentAccess.reduce((acc, user) => {
      const userDoc = documentById.find((doc) => doc.userId === user.userId);
      acc[user.userId] = userDoc
        ? userDoc.permissions
        : {
          ReadRight: false,
          DownloadRight: false,
          EditRight: false,
          DeleteRight: false,
        };
      return acc;
    }, {}),
  };

  return (
    <React.Fragment>
      <Dialog open={props.open} onClose={props.onClose}>
        <DialogTitle sx={{ color: colors.grey[100] }}>
          Assign Permissions
        </DialogTitle>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <DialogContentText sx={{ color: colors.greenAccent[400] }}>
                  Assign Privileges to team members
                </DialogContentText>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    m: "auto",
                    width: "fit-content",
                  }}
                >
                  {documentAccess.map((user) => (
                    <div key={user.userId}>
                      <Typography color={colors.greenAccent[400]}>
                        {user.UserName}
                      </Typography>
                      {[
                        "ReadRight",
                        "EditRight",
                        "DeleteRight",
                        "DownloadRight",
                      ].map((right) => (
                        <FormControlLabel
                          key={`${user.userId}-${right}`} // Unique key for each checkbox
                          control={
                            <Checkbox
                              checked={
                                values.permissions[user.userId]
                                  ? values.permissions[user.userId][right]
                                  : false
                              }
                              onChange={(event) => {
                                setFieldValue(
                                  `permissions.${user.userId}.${right}`,
                                  event.target.checked
                                );
                              }}
                              name={`permissions.${user.userId}.${right}`}
                              color="primary"
                            />
                          }
                          label={`Can ${right.replace("Right", "")}`}
                        />
                      ))}
                    </div>
                  ))}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  color="error"
                  variant="outlined"
                  onClick={props.onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  type="submit"
                  disabled={isSubmitting}
                  color="success"
                >
                  {isSubmitting ? "Assigning..." : "Assign Access"}
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>
    </React.Fragment>
  );
}
export function MarkPaid(props) {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const caseId = JSON.parse(localStorage.getItem("CaseId"));
  const base_url = salesManagement.uri;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const validationSchema = yup.object().shape({
    amountPaid: yup.number().required("Amount Paid is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const mutationData = {
        orderId: props.data.id,

        amountPaid: parseInt(values.amountPaid),
      };

      const response = await generateAndExecuteMutation(
        "markPurchasePaid",
        mutationData,
        "purchase",
        base_url
      );

      if (Object.values(response)[0] != null) {
        props.onClose();
        props.onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Invoice marked as paid successfully.`,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
    setSubmitting(false);
    props.onClose();
  };

  return (
    <React.Fragment>
      <Dialog open={props.open} onClose={props.onClose}>
        <DialogTitle sx={{ color: colors.grey[100] }}>Mark Paid</DialogTitle>
        <Formik
          initialValues={{ amountPaid: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <DialogContentText sx={{ color: colors.greenAccent[400] }}>
                  Enter Amount Paid
                </DialogContentText>
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Amount *"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.amount}
                  name="amountPaid"
                  error={touched.amountPaid && !!errors.amountPaid}
                  helperText={touched.amountPaid && errors.amountPaid}
                  sx={{ gridColumn: "span 2" }}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  color="error"
                  variant="outlined"
                  onClick={props.onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  type="submit"
                  disabled={isSubmitting}
                  color="success"
                >
                  {isSubmitting ? "Marking Paid..." : "Mark Paid"}
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>
    </React.Fragment>
  );
}

export function WarehouseDistributionModal(props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const base_url = salesManagement.uri;

  const validationSchema = yup.object().shape({
    color: yup.string().required("Color is required"),
    cartons: yup
      .number()
      .required("Cartons is required")
      .positive("Must be positive"),
    quantityPerCarton: yup
      .number()
      .required("Quantity per Carton is required")
      .positive("Must be positive"),
    minSize: yup
      .number()
      .required("Min Size is required")
      .positive("Must be positive"),
    maxSize: yup
      .number()
      .required("Max Size is required")
      .positive("Must be positive")
      .min(
        yup.ref("minSize"),
        "Max Size must be greater than or equal to Min Size"
      ),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const mutationData = {
        id: props.data ? props.data.id : null, // For edit; null for create
        purchaseOrderId:
          props.data?.purchaseOrderId ||
          JSON.parse(localStorage.getItem("PurchaseOrderData"))?.id, // Fallback from localStorage if not passed
        color: values.color,
        cartons: parseFloat(values.cartons),
        quantityPerCarton: parseFloat(values.quantityPerCarton),
        minSize: parseFloat(values.minSize),
        maxSize: parseFloat(values.maxSize),
        companyId: JSON.parse(localStorage.getItem("decodedToken"))?.CompanyId, // Assuming token has companyId
      };

      const response = await generateAndExecuteMutation(
        props.isEditing ? "updateDistributionRow" : "createDistributionRow", // Adjust mutation name based on edit/add
        mutationData,
        props.isEditing ? "updatedDistributionRow" : "newDistributionRow",
        base_url
      );

      if (Object.values(response)[0] != null) {
        props.onClose();
        props.onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Warehouse distribution ${props.isEditing ? "updated" : "created"
            } successfully.`,
        });
      }
    } catch (error) {
      console.error("Error processing distribution:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
    setSubmitting(false);
  };

  const initialValues = {
    color: props.data?.color || "",
    cartons: props.data?.cartons || "",
    quantityPerCarton: props.data?.quantityPerCarton || "",
    minSize: props.data?.minSize || "",
    maxSize: props.data?.maxSize || "",
  };

  return (
    <React.Fragment>
      <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: colors.grey[100] }}>
          {props.isEditing
            ? "Edit Warehouse Distribution"
            : "Add Warehouse Distribution"}
        </DialogTitle>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <DialogContentText sx={{ color: colors.greenAccent[400] }}>
                  {props.isEditing
                    ? "Update the distribution details."
                    : "Enter the distribution details for the purchase order."}
                </DialogContentText>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    label="Color *"
                    name="color"
                    variant="filled"
                    value={values.color}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.color && !!errors.color}
                    helperText={touched.color && errors.color}
                  />
                  <TextField
                    fullWidth
                    label="Cartons *"
                    name="cartons"
                    type="number"
                    variant="filled"
                    value={values.cartons}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.cartons && !!errors.cartons}
                    helperText={touched.cartons && errors.cartons}
                  />
                  <TextField
                    fullWidth
                    label="Quantity per Carton *"
                    name="quantityPerCarton"
                    type="number"
                    variant="filled"
                    value={values.quantityPerCarton}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.quantityPerCarton && !!errors.quantityPerCarton
                    }
                    helperText={
                      touched.quantityPerCarton && errors.quantityPerCarton
                    }
                  />
                  <TextField
                    fullWidth
                    label="Min Size *"
                    name="minSize"
                    type="number"
                    variant="filled"
                    value={values.minSize}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.minSize && !!errors.minSize}
                    helperText={touched.minSize && errors.minSize}
                  />
                  <TextField
                    fullWidth
                    label="Max Size *"
                    name="maxSize"
                    type="number"
                    variant="filled"
                    value={values.maxSize}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.maxSize && !!errors.maxSize}
                    helperText={touched.maxSize && errors.maxSize}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  color="error"
                  variant="outlined"
                  onClick={props.onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  type="submit"
                  disabled={isSubmitting}
                  color="success"
                >
                  {isSubmitting
                    ? "Processing..."
                    : props.isEditing
                      ? "Update"
                      : "Create"}
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>
    </React.Fragment>
  );
}
